import {
  EV_IDENTIFICATION_LABELS,
  EV_IDENTIFICATIONS,
  EV_INVOICE_STATUSES,
  EV_PAYMENT_METHOD_LABELS,
  EV_PAYMENT_STATUS_LABELS,
  EV_PAYMENT_STATUSES,
  EV_SESSION_PHASE_LABELS,
  EV_SESSION_PHASE_VALUES,
  type EvSessionSummary,
} from "@station/contracts";
import type { LocalDate } from "@station/core";
import {
  Alert,
  Card,
  DataTable,
  type DataTableColumn,
  DateRangePicker,
  EmptyState,
  FormField,
  MoneyText,
  Select,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { BatteryCharging } from "lucide-react";
import { type ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { formatKwh, sessionWarningText } from "../../../../features/ev/ev-values.js";
import {
  type SessionFilters,
  sessionsQuery,
  useEvChargers,
  useEvSessions,
} from "../../../../features/ev/queries.js";
import {
  evSearchShape,
  EvStationPage,
  type EvStationContext,
  InvoiceStatusBadge,
  PaymentStatusBadge,
  SessionPhaseBadge,
} from "../../../../features/ev/shared.js";
import { compactSearch } from "../../../../features/shifts/shift-values.js";
import { formatDuration } from "../../../../features/wash/wash-values.js";
import { api } from "../../../../lib/api.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../../lib/excel.js";
import { formatDateTime } from "../../../../lib/format.js";

const localDateText = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({
  ...evSearchShape,
  chargerId: z.string().uuid().optional().catch(undefined),
  phase: z.enum(EV_SESSION_PHASE_VALUES).optional().catch(undefined),
  paymentStatus: z.enum(EV_PAYMENT_STATUSES).optional().catch(undefined),
  invoiceStatus: z.enum(EV_INVOICE_STATUSES).optional().catch(undefined),
  source: z.enum(["OCPP", "PARTNER"]).optional().catch(undefined),
  identification: z.enum(EV_IDENTIFICATIONS).optional().catch(undefined),
  from: localDateText,
  to: localDateText,
});
type Search = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/sarj/oturumlar/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: SessionsRoute,
});

/**
 * Şarj oturumları (Prompt 19 web, `GET /ev/sessions` imleçli): istasyon, ünite, evre, ödeme / fatura durumu, kaynak (OCPP /
 * şarj ağı), tanımlama yöntemi ve başlangıç günü süzgeçleri. Kişisel alanlar (müşteri adı, plaka) API'nin maskelediği gibi
 * gösterilir. Satır ayrıntıya (zaman çizelgesi, grafik, tutar kalemleri, ödeme, uyarılar, eylemler) götürür.
 */
function SessionsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <EvStationPage title={t("menu.evSessions")} description={t("ev.sessions.description")}>
      {(context) => <SessionsBody context={context} />}
    </EvStationPage>
  );
}

function identityText(session: EvSessionSummary): string {
  return [session.counterpartyName, session.customerName, session.vehiclePlate]
    .filter((value): value is string => Boolean(value))
    .join(" · ");
}

function SessionsBody({ context }: { context: EvStationContext }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const chargers = useEvChargers(context.station.id);
  const setSearch = (patch: Partial<Search>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });
  const filters: SessionFilters = {
    stationId: context.station.id,
    chargerId: search.chargerId,
    phase: search.phase,
    paymentStatus: search.paymentStatus,
    invoiceStatus: search.invoiceStatus,
    source: search.source,
    identification: search.identification,
    from: search.from,
    to: search.to,
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useEvSessions(filters, pages.cursor);

  const columns = useMemo<DataTableColumn<EvSessionSummary>[]>(
    () => [
      {
        id: "startedAt",
        header: t("ev.fields.startedAt"),
        hideable: false,
        exportValue: (row) => row.startedAt,
        cell: (row) => (
          <Link
            to="/sarj/oturumlar/$id"
            params={{ id: row.id }}
            search={{ station: row.stationId } as never}
            className="tabular whitespace-nowrap underline underline-offset-4"
          >
            {formatDateTime(row.startedAt)}
          </Link>
        ),
      },
      {
        id: "charger",
        header: t("ev.fields.charger"),
        exportValue: (row) =>
          row.source === "PARTNER"
            ? `${row.partnerCode ?? ""} ${row.partnerSessionRef ?? ""}`.trim()
            : `${row.chargerName ?? ""} #${row.connectorNo ?? ""}`,
        cell: (row) =>
          row.source === "PARTNER" ? (
            <span className="grid gap-0.5">
              <span>{t("ev.sessions.partner", { code: row.partnerCode ?? "—" })}</span>
              <span className="font-mono text-xs text-muted-foreground">{row.partnerSessionRef ?? ""}</span>
            </span>
          ) : (
            <span className="grid gap-0.5">
              <span>{row.chargerName ?? "—"}</span>
              <span className="text-xs text-muted-foreground">
                {t("ev.connector.no", { no: row.connectorNo ?? "—" })}
                {row.transactionId !== null ? ` · ${t("ev.sessions.tx", { id: row.transactionId })}` : ""}
              </span>
            </span>
          ),
      },
      {
        id: "identification",
        header: t("ev.fields.identification"),
        exportValue: (row) => EV_IDENTIFICATION_LABELS[row.identification],
        cell: (row) => (
          <span className="grid gap-0.5">
            <span>{EV_IDENTIFICATION_LABELS[row.identification]}</span>
            {identityText(row) ? (
              <span className="text-xs text-muted-foreground">{identityText(row)}</span>
            ) : null}
          </span>
        ),
      },
      {
        id: "phase",
        header: t("ev.fields.phase"),
        exportValue: (row) => EV_SESSION_PHASE_LABELS[row.phase],
        cell: (row) => (
          <span className="grid justify-items-start gap-1">
            <SessionPhaseBadge phase={row.phase} />
            {row.warnings.length > 0 ? (
              <span className="text-xs text-warning" title={row.warnings.map(sessionWarningText).join("\n")}>
                {t("ev.sessions.warningCount", { count: row.warnings.length })}
              </span>
            ) : null}
          </span>
        ),
      },
      {
        id: "energy",
        header: t("ev.fields.energy"),
        align: "right",
        exportValue: (row) => row.energyKwh,
        cell: (row) => <span className="tabular whitespace-nowrap">{formatKwh(row.energyKwh)}</span>,
      },
      {
        id: "durations",
        header: t("ev.sessions.chargingIdle"),
        align: "right",
        exportValue: (row) => `${row.chargingSeconds}/${row.idleSeconds}`,
        cell: (row) => (
          <span className="grid justify-items-end gap-0.5 tabular whitespace-nowrap">
            <span>{formatDuration(row.chargingSeconds)}</span>
            <span className="text-xs text-muted-foreground">
              {t("ev.sessions.idle", { duration: formatDuration(row.idleSeconds) })}
            </span>
          </span>
        ),
      },
      {
        id: "amount",
        header: t("ev.fields.totalAmount"),
        align: "right",
        exportValue: (row) => Number(row.totalAmount) / 100,
        cell: (row) => <MoneyText value={row.totalAmount} />,
      },
      {
        id: "payment",
        header: t("ev.fields.payment"),
        exportValue: (row) => EV_PAYMENT_STATUS_LABELS[row.paymentStatus],
        cell: (row) => (
          <span className="grid justify-items-start gap-1">
            <PaymentStatusBadge status={row.paymentStatus} />
            {row.paymentMethod ? (
              <span className="text-xs text-muted-foreground">
                {EV_PAYMENT_METHOD_LABELS[row.paymentMethod]}
              </span>
            ) : null}
            <InvoiceStatusBadge status={row.invoiceStatus} />
          </span>
        ),
      },
    ],
    [t],
  );

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <DateRangePicker
          label={t("ev.sessions.startDay")}
          value={{
            from: (search.from ?? null) as LocalDate | null,
            to: (search.to ?? null) as LocalDate | null,
          }}
          onValueChange={(next) => setSearch({ from: next.from ?? undefined, to: next.to ?? undefined })}
        />
        <FormField label={t("ev.fields.charger")} className="w-52">
          {(control) => (
            <Select
              {...control}
              value={search.chargerId ?? ""}
              onChange={(event) => setSearch({ chargerId: event.target.value || undefined })}
            >
              <option value="">{t("common.all")}</option>
              {(chargers.data ?? []).map((charger) => (
                <option key={charger.id} value={charger.id}>
                  {charger.name}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("ev.fields.phase")} className="w-44">
          {(control) => (
            <Select
              {...control}
              value={search.phase ?? ""}
              onChange={(event) => setSearch({ phase: (event.target.value || undefined) as Search["phase"] })}
            >
              <option value="">{t("common.all")}</option>
              {EV_SESSION_PHASE_VALUES.map((phase) => (
                <option key={phase} value={phase}>
                  {EV_SESSION_PHASE_LABELS[phase]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("ev.fields.paymentStatus")} className="w-48">
          {(control) => (
            <Select
              {...control}
              value={search.paymentStatus ?? ""}
              onChange={(event) =>
                setSearch({ paymentStatus: (event.target.value || undefined) as Search["paymentStatus"] })
              }
            >
              <option value="">{t("common.all")}</option>
              {EV_PAYMENT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {EV_PAYMENT_STATUS_LABELS[status]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("ev.fields.invoiceStatus")} className="w-44">
          {(control) => (
            <Select
              {...control}
              value={search.invoiceStatus ?? ""}
              onChange={(event) =>
                setSearch({ invoiceStatus: (event.target.value || undefined) as Search["invoiceStatus"] })
              }
            >
              <option value="">{t("common.all")}</option>
              {EV_INVOICE_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {t(`ev.invoiceStatus.${status}`)}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("ev.fields.source")} className="w-40">
          {(control) => (
            <Select
              {...control}
              value={search.source ?? ""}
              onChange={(event) =>
                setSearch({ source: (event.target.value || undefined) as Search["source"] })
              }
            >
              <option value="">{t("common.all")}</option>
              <option value="OCPP">{t("ev.source.OCPP")}</option>
              <option value="PARTNER">{t("ev.source.PARTNER")}</option>
            </Select>
          )}
        </FormField>
        <FormField label={t("ev.fields.identification")} className="w-52">
          {(control) => (
            <Select
              {...control}
              value={search.identification ?? ""}
              onChange={(event) =>
                setSearch({ identification: (event.target.value || undefined) as Search["identification"] })
              }
            >
              <option value="">{t("common.all")}</option>
              {EV_IDENTIFICATIONS.map((identification) => (
                <option key={identification} value={identification}>
                  {EV_IDENTIFICATION_LABELS[identification]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      </Card>
      <DataTable
        caption={t("ev.sessions.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<BatteryCharging />}
            title={t("ev.sessions.emptyTitle")}
            description={t("ev.sessions.emptyDescription")}
          />
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<EvSessionSummary>(
            async (cursor) =>
              (
                await api.GET("/ev/sessions", {
                  params: { query: sessionsQuery(filters, cursor, 200) as never },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: "sarj-oturumlari",
            sheetName: t("menu.evSessions"),
            columns: visible,
            rows,
          });
        }}
      />
    </>
  );
}
